import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, Shield, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { api, BACKEND_URL } from '@/api';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';
import { resolveMediaUrl } from '@/utils/helpers';
import { toast } from 'sonner';
import { SEO } from '@/components/common/SEO';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1626248801379-51a0748a5f96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxzb2NjZXIlMjBtYXRjaCUyMGFjdGlvbiUyMGJsdWUlMjBqZXJzZXl8ZW58MHx8fHwxNzgzMjY4MjA0fDA&ixlib=rb-4.1.0&q=85';

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { viewer, login, register } = useViewerAuth();

  // Form state
  const [mode, setMode] = useState('register'); // register | login | forgot
  const [step, setStep] = useState(1); // 1 = account, 2 = payment (future)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [location, setLocation] = useState('');
  const [watchingInterest, setWatchingInterest] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [allPlans, setAllPlans] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Plan context (from paywall redirect)
  const [plan, setPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const planId = searchParams.get('plan');
  const gameId = searchParams.get('game');
  const returnTo = searchParams.get('return');

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await api.getPlans();
        const fetchedPlans = res.data.plans || [];
        setPlans(fetchedPlans);
        setAllPlans(fetchedPlans);
        if (planId) {
          const found = fetchedPlans.find(p => p.id === planId);
          if (found) setPlan(found);
        }
      } catch { /* silent */ }
    };
    loadPlans();
  }, [planId]);

  // If already logged in and came with a plan, redirect to checkout
  useEffect(() => {
    if (viewer && viewer.id && planId) {
      navigate(returnTo || '/live');
    }
  }, [viewer, planId, returnTo, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'forgot') {
      setLoading(true);
      try {
        await api.viewerForgotPassword(email);
        setResetSent(true);
        toast.success('If an account exists, a reset link has been sent to your email');
      } catch {
        toast.error('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (mode === 'register' && !termsAccepted) {
      return toast.error('Please accept the Terms & Conditions');
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        if (!name.trim()) return toast.error('Name is required');
        const res = await api.viewerRegister({
          email, password, name,
          username: username || undefined,
          location: location || undefined,
          watching_interest: watchingInterest || undefined,
        });
        if (res.data.token) localStorage.setItem('viewer_token', res.data.token);
        toast.success('Account created!');
        // Move to Step 2 (plan selection) instead of redirecting
        setStep(2);
        setLoading(false);
        return;
      }
      // Redirect after login
      if (returnTo) {
        navigate(returnTo);
      } else if (planId) {
        navigate('/live');
      } else {
        navigate('/preferences');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleCheckout = async (selectedPlanId) => {
    setCheckoutLoading(true);
    try {
      const res = await api.subscriptionCheckout({ plan_id: selectedPlanId, origin_url: window.location.origin });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex" data-testid="signup-page">
      <SEO title={mode === 'login' ? 'Sign In | NLS' : 'Sign Up | NLS+'} description="Create your account for New Logical Sports" />

      {/* Left: Form */}
      <div className="flex-1 flex flex-col px-8 sm:px-12 lg:px-16 py-6 overflow-y-auto">
        {/* Logo */}
        <Link to="/" className="mb-4">
          <div className="bg-gray-800 inline-block px-3 py-2 rounded">
            <span className="text-white font-black text-sm tracking-tight leading-none block">NEW LOGICAL</span>
            <span className="text-white font-black text-sm tracking-tight leading-none block">SPORTS+</span>
          </div>
        </Link>

        {/* Step 1: Account Details */}
        <div className={`border border-gray-700 rounded-xl mb-4 transition-all ${step === 2 ? 'p-4 opacity-60' : 'p-6 lg:p-8'}`}>
          {step === 2 ? (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">1. Account created</h2>
                <p className="text-gray-400 text-xs">{email}</p>
              </div>
            </div>
          ) : (
            <>
          <h2 className="text-xl font-bold text-white mb-0.5" data-testid="form-heading">
            {mode === 'login' ? 'Sign In' : mode === 'forgot' ? 'Reset Password' : '1. Account details'}
          </h2>
          <p className="text-gray-400 text-sm mb-4">
            {mode === 'login' ? (
              <>Don't have an account? <button type="button" onClick={() => setMode('register')} className="text-sky-400 hover:text-sky-300" data-testid="switch-to-register">Sign up</button></>
            ) : mode === 'forgot' ? (
              <>Remember your password? <button type="button" onClick={() => { setMode('login'); setResetSent(false); }} className="text-sky-400 hover:text-sky-300">Sign in</button></>
            ) : (
              <>Already have an account? <button type="button" onClick={() => setMode('login')} className="text-sky-400 hover:text-sky-300" data-testid="switch-to-login">Log in</button></>
            )}
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === 'forgot' ? (
              resetSent ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-green-400" />
                  </div>
                  <p className="text-white font-medium mb-1">Check your email</p>
                  <p className="text-gray-400 text-sm">We sent a password reset link to <span className="text-white">{email}</span></p>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-gray-400 text-xs">Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="example@mail.com"
                      required
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      data-testid="forgot-email"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-sky-400 hover:bg-sky-500 text-gray-950 font-semibold py-2.5 text-base"
                    data-testid="forgot-submit"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </>
              )
            ) : mode === 'register' ? (
              <>
                {/* Row 1: Email + Full Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400 text-xs">Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="example@mail.com"
                      required
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      data-testid="signup-email"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Full Name</Label>
                    <Input
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="First and last name"
                      required
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      data-testid="signup-name"
                    />
                  </div>
                </div>

                {/* Row 2: Where from + Watching Interest */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400 text-xs">Where are you from? (City, State/County, Country)</Label>
                    <Input
                      value={location}
                      onChange={e => setLocation(e.target.value)}
                      placeholder=""
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      data-testid="signup-location"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Who are you signing up to watch? (Club / league)</Label>
                    <Input
                      value={watchingInterest}
                      onChange={e => setWatchingInterest(e.target.value)}
                      placeholder=""
                      className="bg-gray-900 border-gray-700 text-white mt-1"
                      data-testid="signup-watching"
                    />
                  </div>
                </div>

                {/* Row 3: Password */}
                <div>
                  <Label className="text-gray-400 text-xs">Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Password"
                      required
                      minLength={6}
                      className="bg-gray-900 border-gray-700 text-white pr-10"
                      data-testid="signup-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="border-gray-600 data-[state=checked]:bg-sky-500 mt-0.5"
                    data-testid="signup-terms"
                  />
                  <label className="text-gray-400 text-sm cursor-pointer" onClick={() => setTermsAccepted(!termsAccepted)}>
                    Yes, I agree to the <span className="text-sky-400 hover:underline">Terms & Conditions</span>
                  </label>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-gray-400 text-xs">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="example@mail.com"
                    required
                    className="bg-gray-900 border-gray-700 text-white mt-1"
                    data-testid="signup-email"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Password</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Password"
                      required
                      minLength={6}
                      className="bg-gray-900 border-gray-700 text-white pr-10"
                      data-testid="signup-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <button type="button" onClick={() => setMode('forgot')} className="text-sky-400 hover:text-sky-300 text-xs" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
              </>
            )}

            <Button
              type="submit"
              disabled={loading || (mode === 'register' && !termsAccepted)}
              className="w-full bg-sky-400 hover:bg-sky-500 text-gray-950 font-semibold py-2.5 text-base"
              data-testid="signup-submit"
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Continue'}
            </Button>
          </form>

          {/* Social Login Divider */}
          <div className="my-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-700" />
            <span className="text-gray-500 text-xs">or continue with</span>
            <div className="flex-1 h-px bg-gray-700" />
          </div>

          {/* Social Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-700 bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-gray-300 text-sm font-medium">Google</span>
            </button>

            <button
              type="button"
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg border border-gray-700 bg-gray-800/50 hover:bg-gray-700/50 transition-colors opacity-60 cursor-not-allowed"
              title="Coming soon"
              data-testid="apple-login-btn"
            >
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="text-gray-400 text-sm font-medium">Apple ID</span>
            </button>
          </div>
          </>
          )}
        </div>

        {/* Step 2: Payment (active after account creation) */}
        {step === 2 && (
          <div className="border border-gray-700 rounded-xl p-6 lg:p-8 mb-4">
            <h2 className="text-xl font-bold text-white mb-0.5">2. Choose a membership</h2>
            <p className="text-gray-400 text-sm mb-4">
              Select a plan to unlock premium content, or <button type="button" onClick={() => navigate('/preferences')} className="text-sky-400 hover:text-sky-300">skip for now</button>
            </p>
            {allPlans.length > 0 ? (
              <div className="space-y-3">
                {allPlans.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleCheckout(p.id)}
                    disabled={checkoutLoading}
                    className={`w-full flex items-center justify-between p-4 rounded-lg border transition-colors text-left ${plan?.id === p.id ? 'border-sky-400 bg-sky-400/10' : 'border-gray-700 hover:border-gray-500 bg-gray-800/50'}`}
                    data-testid={`plan-select-${p.id}`}
                  >
                    <div>
                      <p className="text-white font-semibold">{p.name}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{p.interval === 'month' ? 'Billed monthly' : p.interval === 'year' ? 'Billed annually' : p.interval}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold">${p.price?.toFixed(2)}</p>
                      <p className="text-gray-500 text-xs">/{p.interval === 'month' ? 'mo' : 'yr'}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No plans available</p>
            )}
            {checkoutLoading && <p className="text-sky-400 text-sm mt-3 text-center">Redirecting to checkout...</p>}
          </div>
        )}

        {step === 1 && plan && mode === 'register' && (
          <div className="border border-gray-700 rounded-xl p-6 opacity-50">
            <h2 className="text-lg font-bold text-white">2. Payment details</h2>
            <p className="text-gray-500 text-sm mt-1">Complete account creation first</p>
          </div>
        )}
      </div>

      {/* Right: Plan Summary Sidebar */}
      <div className="hidden lg:flex flex-col w-[480px] xl:w-[520px] bg-gray-900/50 border-l border-gray-800 px-10 xl:px-14 py-10 sticky top-0 h-screen">
        {/* Hero Image */}
        <div className="rounded-xl overflow-hidden mb-8 flex-shrink-0">
          <img
            src={HERO_IMAGE}
            alt="Live sports"
            className="w-full h-64 object-cover"
          />
        </div>

        {plan ? (
          /* Show selected plan */
          <div>
            <p className="text-sky-400 text-xs font-bold tracking-widest uppercase mb-1">MEMBERSHIP</p>
            <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
            <p className="text-gray-400 text-sm mb-6">{plan.description}</p>

            {plan.features && plan.features.length > 0 && (
              <ul className="space-y-2 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            )}

            <div className="border-t border-gray-700 pt-4 mt-auto">
              <div className="flex items-end justify-between">
                <span className="text-lg font-bold text-white">Total</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-white">USD ${plan.price?.toFixed(2)}</span>
                  <span className="text-gray-400 text-sm block">/{plan.interval}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 justify-center mt-4 text-emerald-400 text-sm">
                <Shield className="w-4 h-4" />
                Secure checkout
              </div>
            </div>
          </div>
        ) : (
          /* Show all available plans */
          <div>
            <p className="text-sky-400 text-xs font-bold tracking-widest uppercase mb-3">MEMBERSHIP OPTIONS</p>
            <div className="space-y-3">
              {plans.map(p => (
                <button
                  key={p.id}
                  onClick={() => setPlan(p)}
                  className="w-full text-left p-4 rounded-lg border border-gray-700 hover:border-sky-500 transition-colors bg-gray-800/30"
                  data-testid={`plan-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold text-sm">{p.name}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{p.description?.slice(0, 60)}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-white font-bold">${p.price?.toFixed(2)}</span>
                      <span className="text-gray-500 text-xs block">/{p.interval}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-gray-500 text-xs text-center mt-4">Free account — just sign up to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupPage;
